<script>
import ModelCreate from "../models/create-v2";
import FormsModel from "@/components/forms/purchase_plan";
import purchasePlanSubmitError from "@/components/mixins/purchase-plan-submit-error";

export default {
  extends: ModelCreate,
  mixins: [purchasePlanSubmitError],
  page: {
    title: "Create Purchase Plan",
  },
  props: {
    cardTitle: {default() { return this.lodash.startCase(this.$t('purchase_plans.singular')) }},
    resourceRoute: {default: 'purchase_plans'},
    modelVariable: {default: 'purchase_plan'},
    form: {default() { return FormsModel; }}
  },
  data() {
    return {
      confirmPublishResolve: null,
      hasLimitValidationIssue: false,
      formListeners: {
        'limit-validation-change': (value) => { this.hasLimitValidationIssue = value; },
      },
      title: this.lodash.upperFirst(this.$t('purchase_plans.plural')),
      model: {
        ...this.$route.query,
        ...((this.$route.params || {}).prefill || {}),
      },
      items: [
        {
          text: this.lodash.upperFirst(this.$t('purchase_plans.plural')),
          href: "/purchase_plans",
        },
        {
          text: this.$t("Create New"),
          href: "/purchase_plans/create",
          active: true,
        },
      ]
    }
  },
  methods: {
    getModelName(value) {
      return (value.user || {}).email || this.getModelKey(value)
    },
    normalizePayload(value = {}) {
      const payload = {
        ...value,
        _action: value._action || 'draft',
      };

      if (!payload.name) {
        const submittedAt = payload.submitted_at || this.$moment().format('YYYY-MM-DD');
        const productType = (payload.product_type || 'x').toUpperCase();
        payload.name = `PP-${productType}-${submittedAt}`;
      }

      if ((payload.product_type || '').toLowerCase() === 'x') {
        payload.sales_plan_id = null;
      }

      delete payload.product_type;
      delete payload.ticket;
      delete payload.ticketable;

      this.lastPurchasePlanSubmitAction = payload._action;

      return payload;
    },
    store(value) {
      return this.$axios
        .post('/v1/purchase_plans', this.normalizePayload(value))
        .catch((e) => {
          if (e.response && e.response.data) {
            this.errors = e.response.data.errors;
          }
          throw e
        });
    },
    handleStore(value) {
      this.storing = true;
      this.store(value)
        .then(() => this.storeSuccessAlert(this.getModelName(value)))
        .then(() => {
          this.isEditing = false;
          return this.$router.push('/purchase_plans');
        })
        .catch((error) => this.failedAlert(error))
        .finally(() => { this.storing = false });
    },
    confirmPublish() {
      return new Promise((resolve) => {
        this.$bvModal.show('confirm-publish-modal');
        this.confirmPublishResolve = (confirmed) => resolve({ value: confirmed });
      });
    },
    onModalConfirm() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(true);
        this.confirmPublishResolve = null;
      }
    },
    onModalCancel() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(false);
        this.confirmPublishResolve = null;
      }
    },
    onModalHide() {
      if (this.confirmPublishResolve) {
        this.confirmPublishResolve(false);
        this.confirmPublishResolve = null;
      }
    },
    submitWithAction(action) {
      if (action === 'publish' && this.hasLimitValidationIssue) {
        return;
      }

      if (!this.$refs.modelCreate || !this.$refs.modelCreate.$refs.form || !this.$refs.modelCreate.$refs.form.validateAll) {
        return;
      }

      this.$refs.modelCreate.$refs.form.validateAll().then((isValid) => {
        if (!isValid) return;

        const model = {
          ...this.model,
          _action: action,
        };

        if (action === 'publish') {
          this.confirmPublish().then((response) => {
            if (!response.value) return;
            this.handleStore(model);
          });
          return;
        }

        this.handleStore(model);
      });
    }
  }
}
</script>

<template>
  <Layout>
    <PageHeader :title="title" :items="items" />
    <div class="row">
      <model-create
        ref="modelCreate"
        class="col-12"
        :loading="loading"
        :storing="storing"
        v-bind="$props"
        v-model="model"
        :errors.sync="errors"
        :form-listeners="formListeners"
        @back-click="handleBack"
        @store-click="handleStore"
        @model-changed="handleModelChanged"
      >
        <template #card-footer="{ storing, denyBack, handleBack }">
          <div class="row footer-wrapper">
            <div class="col-12 d-flex flex-row-reverse justify-content-start flex-wrap">
              <b-button :disabled="storing || hasLimitValidationIssue" class="ms-2" variant="primary" @click="submitWithAction('publish')">
                <i v-if="storing" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ $t('purchase_plans.publish') }}
              </b-button>
              <b-button :disabled="storing" class="ms-2 border-0" variant="primary" @click="submitWithAction('draft')" style="background-color: #FDB022">
                <i v-if="storing" class="bx bx-loader bx-spin font-size-16 align-middle me-2"></i>
                {{ lodash.startCase($t('purchase_plans.save_as_draft')) }}
              </b-button>
              <b-button class="ms-2" v-if="!denyBack" variant="link" @click="handleBack">
                {{ $t('Back') }}
              </b-button>
            </div>
          </div>
        </template>
      </model-create>
    </div>

    <b-modal
      id="confirm-publish-modal"
      ref="confirmPublishModal"
      centered
      hide-header
      no-close-on-backdrop
      body-class="p-4"
      footer-class="p-0 border-0"
      @ok="onModalConfirm"
      @cancel="onModalCancel"
      @hide="onModalHide"
    >
      <div class="text-center py-2">
        <div class="mb-2 d-flex justify-content-center">
          <div class="d-flex justify-content-center align-items-center p-2 rounded rounded-circle bg-green-200" style="width: 48px; height: 48px;">
            <div class="d-flex justify-content-center align-items-center p-2 rounded rounded-circle" style="background: #15803D; width: 24px; height: 24px;">
              <i class="bx bx-check text-green-200" style="font-size: 24px;"></i>
            </div>
          </div>
        </div>
        <h4 class="mb-2">{{ $t('purchase_plans.confirm_publish_title') }}</h4>
        <p class="text-muted mb-0">{{ $t('purchase_plans.confirm_publish_text') }}</p>
      </div>
      <template #modal-footer="{ ok, cancel }">
        <div class="d-flex gap-3 w-100 p-3">
          <b-button variant="light" class="w-50" @click="cancel()">
            {{ $t('purchase_plans.cancel') }}
          </b-button>
          <b-button variant="primary" class="w-50" @click="ok()">
            {{ $t('purchase_plans.yes_continue') }}
          </b-button>
        </div>
      </template>
    </b-modal>

    <purchase-plan-publish-limit-dialog
        ref="purchasePlanPublishLimitDialog"
    />
  </Layout>
</template>

<style scoped lang="scss">
// ::v-deep {
//   .modal-content {
//     overflow: hidden;
//   }
// }
</style>
